import { useEffect, useRef } from "react";
import _ from "lodash";
import { useFormik } from "formik";
import getValidation from "./validation";
import { setMaxNumberOfDevices, setRangeWithUnits, setStepCont, useTypedDispatch } from "../../../app/state";
import { Button, Grid, TextField } from "@material-ui/core";
import UnitsInput from "../../../components/TextFields/UnitsInput";
import { extractUnits } from "../../../helpers/converters/units";
import { convertHzIntoUnit, convertToHz } from "../../../helpers/converters/frequency";
import { floatToString } from "../../../helpers/math";
import styles from "./index.module.scss";

const FrequencyAnalysisForm = ({ onSubmit }) => {
  const dispatch = useTypedDispatch();
  const inputRef = useRef<HTMLDivElement>();
  const rangeFormik = useFormik({
    initialValues: {
      frequencyFrom: '',
      frequencyTo: '',
      stepCount: 2,
      stepFrequencyWidth: '',
      maxNumberOfDevicesAllowed: '',
    },
    onSubmit: () => {},
    validationSchema: getValidation(),
    validateOnChange: false,
  });

  useEffect(() => {
    inputRef?.current?.focus();
  }, []);

  const calcFrequencyTo = () => {
    const start = rangeFormik.values.frequencyFrom;
    const stepCount = rangeFormik.values.stepCount;
    const stepWidth = rangeFormik.values.stepFrequencyWidth;

    if (!start || !stepCount || !stepWidth) {
      return;
    }

    const currentUnits = extractUnits(start);
    const startInHz = convertToHz(start);
    const stepWidthInHz = convertToHz(stepWidth);
    const endInHz = startInHz + stepWidthInHz * stepCount;

    const end = `${floatToString(convertHzIntoUnit(endInHz, currentUnits))} ${currentUnits}`;

    rangeFormik.setFieldValue('frequencyTo', end);
  }

  const onSelectRange = async () => {
    const valid = await rangeFormik.validateForm();

    if (_.isEmpty(valid)) {
      const range = [ rangeFormik.values.frequencyFrom, rangeFormik.values.frequencyTo ];

      dispatch(setRangeWithUnits(range));
      dispatch(setStepCont(rangeFormik.values.stepCount));
      dispatch(setMaxNumberOfDevices(rangeFormik.values.maxNumberOfDevicesAllowed));

      onSubmit(range);
    }
  }

  return (
    <Grid container className={styles.container}>
      <Grid container spacing={2}>
        <Grid item xs={6}>
          <UnitsInput
            inputRef={inputRef}
            autoFocus={true}
            unitList={UnitsInput.UNIT_TEMPLATE.FREQUENCY}
            fullWidth
            required
            name='frequencyFrom'
            variant='outlined'
            label={'Frequency range from'}
            onBlur={calcFrequencyTo}
            onChange={rangeFormik?.handleChange}
            value={rangeFormik?.values?.frequencyFrom}
            error={Boolean(rangeFormik?.errors?.frequencyFrom)}
            helperText={rangeFormik?.errors?.frequencyFrom}
          />
        </Grid>
        <Grid item xs={6}>
          <TextField
            fullWidth
            name='frequencyTo'
            variant='outlined'
            label={'Frequency range to'}
            disabled={true}
            onChange={rangeFormik?.handleChange}
            value={rangeFormik?.values?.frequencyTo}
            error={Boolean(rangeFormik?.errors?.frequencyTo)}
            helperText={rangeFormik?.errors?.frequencyTo}
          />
        </Grid>
        <Grid item xs={12}>
          <UnitsInput
            unitList={UnitsInput.UNIT_TEMPLATE.FREQUENCY}
            required
            fullWidth
            name='stepFrequencyWidth'
            variant='outlined'
            label={'Step width'}
            onBlur={calcFrequencyTo}
            onChange={rangeFormik?.handleChange}
            value={rangeFormik?.values?.stepFrequencyWidth}
            error={Boolean(rangeFormik?.errors?.stepFrequencyWidth)}
            helperText={rangeFormik?.errors?.stepFrequencyWidth}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label='Step count'
            variant='outlined'
            name='stepCount'
            required
            fullWidth
            onBlur={calcFrequencyTo}
            value={rangeFormik.values.stepCount}
            onChange={rangeFormik.handleChange}
            helperText={rangeFormik.errors.stepCount}
            error={Boolean(rangeFormik.errors.stepCount)}
          />
        </Grid>
        <Grid item xs={12}>
          <TextField
            label='Max number of devices allowed'
            variant='outlined'
            name='maxNumberOfDevicesAllowed'
            fullWidth
            value={rangeFormik.values.maxNumberOfDevicesAllowed}
            onChange={rangeFormik.handleChange}
            helperText={rangeFormik.errors.maxNumberOfDevicesAllowed}
            error={Boolean(rangeFormik.errors.maxNumberOfDevicesAllowed)}
          />
        </Grid>
      </Grid>
      <Grid item xs={12}>
        <Button
          variant="contained"
          color="primary"
          type="submit"
          className={styles.button}
          disabled={
            !rangeFormik.values.frequencyFrom ||
            !rangeFormik.values.frequencyTo
          }
          onClick={onSelectRange}
        >
          Show
        </Button>
      </Grid>
    </Grid>
  )
}

export default FrequencyAnalysisForm;